import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { useSmartEntity } from 'contexts/smart-entity'
import { useMe } from 'contexts/me'
import { useAppSignal } from 'hooks/app-signal'
import { useCheckDocumentPermission, useCombineAccessUnits, splitAccessUnits } from 'pages/documents/utilities'
import { size, omit } from 'utilities/object'
import { compact } from 'utilities/array'
import { Fields } from './s'
import Form from 'components/form/controller'
import StringField from 'components/form/field/string'
import SelectField from 'components/form/field/select'
import TextField from 'components/form/field/text'
import AccessUnitsField from 'components/form/field/access-units'
import QuickSelectAccessUnits from 'pages/documents/components/quick-select-access-units'
import Message from 'components/message'
import PersonField from 'components/form/field/person'
import LanguageField from 'components/form/field/language'
import ContentLanguageSupportMessage from 'pages/documents/components/content-language-support-message'
import CountryField from 'components/form/field/country-picker'
import CheckboxField from 'components/form/field/checkbox'
import AccessWarnings from 'pages/documents/components/access-warnings'
import Caption from 'components/typography/caption'
import DocumentReferences from './document-references'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'
import { get } from 'api'

const PreviewSmartDocumentDetailsFields = ({
    dokument,
    editing, setEditing,
    categories = [],
    previewAnother,
    salt
}) => {
    const { formatMessage } = useIntl()

    const { updateDocumentLocally: updateDocumentInList } = useDocuments()
    const { update: updateSmartDocument } = useSmartEntity()

    const { getListMe } = useMe()

    const checkDocumentPermission = useCheckDocumentPermission()
    const combineAccessUnits = useCombineAccessUnits()

    const [concerns, setConcerns] = useState(dokument.concerns ?? null)
    const [whistle, setWhistle] = useState(Date.now())
    const [access, setAccess] = useState({
        value: combineAccessUnits(dokument),
        changed: false
    })

    const [language, setLanguage] = useState(dokument.language ?? null)
    const [updating, setUpdating] = useState(false)
    const [documentReferences, setDocumentReferences] = useState([])
    const [documentReferencesUpdatedAt, setDocumentReferencesUpdatedAt] = useState(0)

    useAppSignal({
        namespace: 'smart-document:references:update',
        action: id => {
            if(dokument.id !== id) {
                return
            }

            setDocumentReferencesUpdatedAt(Date.now())
        }
    })

    useEffect(() => {
        if(!dokument?.id) {
            return
        }

        const fetchDocumentReferences = async () => {
            const { ok, response } = await get({
                path: '/documents',
                params: { documentRefId: dokument.id }
            })

            if(ok && response) {
                setDocumentReferences(response.items)
            }
        }

        fetchDocumentReferences()
    }, [dokument?.id, documentReferencesUpdatedAt])

    const update = async (body, { resetTouched }) => {
        setUpdating(true)

        const { ok, response } = await updateSmartDocument({
            ...omit(body, 'access'),
            ...(access.changed ? splitAccessUnits(access.value) : null),
            ...((body.category === 'none') ? { category: null } : null)
        })

        setUpdating(false)

        if(ok) {
            updateDocumentInList(response, dokument.id)
            resetTouched()
            setEditing(null)
        }
    }

    const { editable } = checkDocumentPermission(dokument)
    const noOwners = !access.value.some(({ access }) => access.owner)

    return (
        <Form
            layout="vertical"
            onSubmit={update}>
            {({ values, touched, errors, trigger }) => (
                <>
                    <Fields>
                        {!!editing && (
                            <>
                                <StringField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_name',
                                        defaultMessage: 'Name'
                                    })}
                                    name="name"
                                    field={{
                                        value: dokument.name,
                                        required: true
                                    }}
                                    controlProps={{ autoFocus: editing === 'auto' }} />
                                <SelectField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_category',
                                        defaultMessage: 'Category'
                                    })}
                                    name="category"
                                    field={{
                                        value: dokument.category ?? 'none',
                                        include: 'touched',
                                        allowEmptyOption: false
                                    }}
                                    options={categories.map(category => ({
                                        ...category,
                                        key: category.value
                                    }))} />
                                <TextField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_description',
                                        defaultMessage: 'Description'
                                    })}
                                    name="description"
                                    field={{ value: dokument.description }}
                                    controlProps={{
                                        rows: 4
                                    }} />
                            </>
                        )}
                        {(!!concerns || editing) && (
                            <PersonField
                                salt={salt}
                                label={formatMessage({
                                    id: 'document_label_regarding',
                                    defaultMessage: 'Regarding'
                                })}
                                name="concerns"
                                field={{
                                    value: concerns,
                                    editable: editing,
                                    include: 'touched',
                                    optional: formatMessage({
                                        id: 'document_label_regarding_optional',
                                        defaultMessage: 'Optional: Specify who the document regards'
                                    })
                                }}
                                picker={{ outer: false }}
                                onChange={({ concerns }) => setConcerns(concerns)} />
                        )}
                        <AccessUnitsField
                            salt={salt}
                            label={formatMessage({
                                id: 'noun_access',
                                defaultMessage: 'Access'
                            })}
                            name="access"
                            field={{
                                value: access.value,
                                editable: editing,
                                include: access.changed ?
                                    'always' :
                                    'touched'
                            }}
                            controlProps={{ autoFocus: editing === 'access' }}
                            {...(!!editable ? {
                                display: {
                                    view: (
                                        <Plain
                                            onClick={() => setEditing('access')}
                                            className="constructive">
                                            <FormattedMessage
                                                id="access_action_manage"
                                                defaultMessage="Manage access" />
                                        </Plain>
                                    )
                                }
                            } : null)}
                            formatAdminTooltip={unit => formatMessage({
                                id: 'document_unit_administrator_description',
                                defaultMessage: 'Users with roles that grant document access in <strong>{name}</strong>'
                            }, unit)}
                            belowSearchContent={(
                                <QuickSelectAccessUnits
                                    options={compact([
                                        ...(concerns ? [{
                                            unit: concerns,
                                            access: {
                                                share: true,
                                                owner: false
                                            }
                                        }] : []),
                                        {
                                            unit: getListMe(),
                                            access: {
                                                share: true,
                                                owner: true
                                            }
                                        }
                                    ])}
                                    values={access.value}
                                    insertValue={value => {
                                        setAccess({
                                            value: [
                                                value,
                                                ...access.value
                                            ],
                                            changed: true
                                        })

                                        setWhistle(Date.now())
                                    }} />
                            )}
                            onChange={({ access }) => setAccess({
                                value: access,
                                changed: true
                            })}
                            whistle={whistle} />
                        {!!editing && (
                            <AccessWarnings
                                access={access}
                                setAccess={access => {
                                    setAccess(access)
                                    setWhistle(Date.now())
                                }}>
                                <Message
                                    type="info"
                                    textClassName="preserve-line-breaks"
                                    title={formatMessage({
                                        id: 'documents_smart_access_information_title',
                                        defaultMessage: 'Smart documents are dynamic in nature'
                                    })}
                                    message={formatMessage({
                                        id: 'documents_smart_access_information',
                                        defaultMessage: 'A smart document is dynamic in nature, and can appear different to people depending on their permissions – or as its data sources change over time.\n\nIf your purpose is to share this document with an employee, it’s better to store it as a PDF document, and share that instead. This will ‘seal’ the document with all the current values, exactly like you see them now.'
                                    })}
                                    name="smart-documents:dynamic-in-nature:message"
                                    dismissedProps={{ className: 'spacious' }}
                                    key="smart-documents:dynamic-in-nature:message" />
                            </AccessWarnings>
                        )}
                        {!!documentReferences?.length && (
                            <DocumentReferences
                                dokuments={documentReferences}
                                preview={previewAnother}
                                salt={salt} />
                        )}
                        <LanguageField
                            salt={salt}
                            label={formatMessage({
                                id: 'noun_language',
                                defaultMessage: 'Language'
                            })}
                            name="language"
                            field={{
                                value: language,
                                editable: editing,
                                unsettable: false,
                                required: true
                            }}
                            onChange={({ language }) => setLanguage(language)} />
                        <ContentLanguageSupportMessage language={language} tight />
                        <CountryField
                            salt={salt}
                            {...(!editing ? { className: 'compact' } : null)}
                            label={formatMessage({
                                id: 'noun_legislation',
                                defaultMessage: 'Legislation'
                            })}
                            name="legislation"
                            field={{
                                value: dokument.legislation ?? null,
                                editable: editing
                            }}
                            view={{ showFlag: true }} />
                        {!!editing && (
                            <>
                                <CheckboxField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'notification_toggle_label_update',
                                        defaultMessage: 'Send notification when updating'
                                    })}
                                    name="notify"
                                    field={{
                                        value: false,
                                        include: 'always'
                                    }}
                                    interaction="switch" />
                                {!!values?.notify && (
                                    <Caption>
                                        <FormattedMessage
                                            id="notification_toggle_document_upload_description"
                                            defaultMessage="A notification will be sent to those the document will be shared with and who has opted to receive notifications." />
                                    </Caption>
                                )}
                            </>
                        )}
                    </Fields>
                    {(!!editable && !!editing) && (
                        <Actions>
                            <Plain
                                onClick={() => {
                                    setEditing(false)

                                    setAccess({
                                        value: combineAccessUnits(dokument),
                                        changed: false
                                    })
                                }}
                                className="neutral"
                                disabled={updating}>
                                <FormattedMessage
                                    id="action_cancel"
                                    defaultMessage="Cancel" />
                            </Plain>
                            <ButtonSubmit
                                className={`constructive${updating ? ' loading' : ''}`}
                                disabled={(!touched.length && !access.changed) || !!size(errors) || noOwners || updating}
                                ref={trigger}>
                                <FormattedMessage
                                    id="action_save"
                                    defaultMessage="Save" />
                            </ButtonSubmit>
                        </Actions>
                    )}
                </>
            )}
        </Form>
    )
}

export default PreviewSmartDocumentDetailsFields